'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Clock3, CalendarDays } from 'lucide-react'

import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface WeeklyAvailabilityProps {
  daysPerWeekName?: string
  hoursPerDayName?: string
  weeklyHoursName?: string

  defaultDaysPerWeek?: number
  defaultHoursPerDay?: number

  required?: boolean
  disabled?: boolean
}

const DAY_OPTIONS = [2, 3, 4, 5] as const

const HOURS_PER_DAY_OPTIONS = [1, 2, 3, 4, 5, 6, 7] as const

const WeeklyAvailability: React.FC<WeeklyAvailabilityProps> = ({
  daysPerWeekName = 'role.psychology_internship.days_per_week',
  hoursPerDayName = 'role.psychology_internship.hours_per_day',
  weeklyHoursName = 'role.psychology_internship.weekly_hours',

  defaultDaysPerWeek,
  defaultHoursPerDay,

  required = true,
  disabled = false,
}) => {
  const [daysPerWeek, setDaysPerWeek] = useState<string>(
    defaultDaysPerWeek ? String(defaultDaysPerWeek) : '',
  )

  const [hoursPerDay, setHoursPerDay] = useState<string>(
    defaultHoursPerDay ? String(defaultHoursPerDay) : '',
  )

  const weeklyHours = useMemo(() => {
    const days = Number(daysPerWeek)
    const hours = Number(hoursPerDay)

    if (
      !Number.isInteger(days) ||
      !Number.isInteger(hours) ||
      days < 2 ||
      days > 5 ||
      hours < 1 ||
      hours > 7
    ) {
      return ''
    }

    return String(days * hours)
  }, [daysPerWeek, hoursPerDay])

  const hasCompleteAvailability =
    Boolean(daysPerWeek) &&
    Boolean(hoursPerDay) &&
    Boolean(weeklyHours)

  const daysInputRef = useRef<HTMLInputElement>(null)
  const hoursInputRef = useRef<HTMLInputElement>(null)
  const weeklyInputRef = useRef<HTMLInputElement>(null)

  // React updating a hidden input's value does not emit a native bubbling input
  // event, so dispatch one whenever each canonical value changes. This lets
  // FormClient's delegated input/change validation re-run for these fields.
  useEffect(() => {
    daysInputRef.current?.dispatchEvent(
      new Event('input', {
        bubbles: true,
      }),
    )
  }, [daysPerWeek])

  useEffect(() => {
    hoursInputRef.current?.dispatchEvent(
      new Event('input', {
        bubbles: true,
      }),
    )
  }, [hoursPerDay])

  useEffect(() => {
    weeklyInputRef.current?.dispatchEvent(
      new Event('input', {
        bubbles: true,
      }),
    )
  }, [weeklyHours])

  return (
    <div className="space-y-4">
      {/* ─────────────────────────────────────────────
          Context / operating hours
          ───────────────────────────────────────────── */}

      <div className="rounded-lg border bg-muted/30 p-3 sm:p-4">
        <div className="flex items-start gap-3">
          <Clock3
            className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />

          <div className="space-y-1">
            <p className="text-sm font-medium">
              Internship availability
            </p>

            <p className="text-xs leading-relaxed text-muted-foreground sm:text-sm">
              ReACH internship hours fall between{' '}
              <span className="font-medium text-foreground">
                10:00 AM and 5:00 PM, Monday to Friday
              </span>
              . Interns must be available for at least{' '}
              <span className="font-medium text-foreground">
                2 days per week
              </span>
              .
            </p>

            <p className="text-xs leading-relaxed text-muted-foreground">
              You may attend for up to 7 hours on any one day.
            </p>
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────
          Availability controls

          Each canonical hidden input lives INSIDE the same `.grid gap-*` wrapper
          as the visible control it represents, so FormClient can resolve the
          field's wrapper (via closest('.grid[class*="gap-"]')) and its Radix
          Select trigger (button[role="combobox"]) for inline errors and red
          styling. The Radix Selects deliberately carry no `name` so there is
          exactly one canonical submitted value per field.
          ───────────────────────────────────────────── */}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="grid gap-1.5 sm:gap-2">
          <Label className="text-sm">
            Days per week
            {required && (
              <span
                className="text-red-500"
                aria-hidden="true"
              >
                {' '}
                *
              </span>
            )}
          </Label>

          <input
            ref={daysInputRef}
            type="hidden"
            name={daysPerWeekName}
            value={daysPerWeek}
            readOnly
            disabled={disabled}
          />

          <Select
            value={daysPerWeek}
            onValueChange={setDaysPerWeek}
            disabled={disabled}
          >
            <SelectTrigger className="w-full text-sm">
              <SelectValue placeholder="Select days per week…" />
            </SelectTrigger>

            <SelectContent
              position="popper"
              side="bottom"
              align="start"
            >
              {DAY_OPTIONS.map((days) => (
                <SelectItem
                  key={days}
                  value={String(days)}
                >
                  {days} days per week
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <p className="text-[11px] text-muted-foreground sm:text-xs">
            Minimum 2 days, maximum 5 days per week.
          </p>
        </div>

        <div className="grid gap-1.5 sm:gap-2">
          <Label className="text-sm">
            Average hours per day
            {required && (
              <span
                className="text-red-500"
                aria-hidden="true"
              >
                {' '}
                *
              </span>
            )}
          </Label>

          <input
            ref={hoursInputRef}
            type="hidden"
            name={hoursPerDayName}
            value={hoursPerDay}
            readOnly
            disabled={disabled}
          />

          <Select
            value={hoursPerDay}
            onValueChange={setHoursPerDay}
            disabled={disabled}
          >
            <SelectTrigger className="w-full text-sm">
              <SelectValue placeholder="Select hours per day…" />
            </SelectTrigger>

            <SelectContent
              position="popper"
              side="bottom"
              align="start"
            >
              {HOURS_PER_DAY_OPTIONS.map((hours) => (
                <SelectItem
                  key={hours}
                  value={String(hours)}
                >
                  {hours} {hours === 1 ? 'hour' : 'hours'} per day
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <p className="text-[11px] text-muted-foreground sm:text-xs">
            Maximum 7 hours per day within ReACH&apos;s operating hours.
          </p>
        </div>
      </div>

      {/* ─────────────────────────────────────────────
          Derived weekly availability

          Wrapped in a `.grid gap-*` so FormClient can resolve a wrapper for the
          derived weekly_hours hidden input and render any inconsistency/tamper
          error in a sensible place. The visual card is unchanged.
          ───────────────────────────────────────────── */}

      <div className="grid gap-1.5 sm:gap-2">
        <input
          ref={weeklyInputRef}
          type="hidden"
          name={weeklyHoursName}
          value={weeklyHours}
          readOnly
          disabled={disabled}
        />

        <div
          className={[
            'rounded-lg border p-3 transition-colors sm:p-4',
            hasCompleteAvailability
              ? 'bg-primary/5'
              : 'bg-muted/20',
          ].join(' ')}
          aria-live="polite"
        >
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-background shadow-sm">
            <CalendarDays
              className="h-4 w-4 text-muted-foreground"
              aria-hidden="true"
            />
          </div>

          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">
              Estimated weekly availability
            </p>

            {hasCompleteAvailability ? (
              <p className="text-base font-semibold">
                {weeklyHours} hours per week
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                Select your days and hours to calculate this.
              </p>
            )}
          </div>
        </div>

          {hasCompleteAvailability && (
            <p className="mt-2 text-xs text-muted-foreground">
              {daysPerWeek} days × {hoursPerDay} hours per day
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default WeeklyAvailability
